/**
 * Expediente Digital de Empleado
 * Fecha: 11 Nov 2025
 *
 * Vista completa para gestión de expediente digital con tabs,
 * aprobación/rechazo y control de versiones
 */

import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'
import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { employeeDocumentsService } from '@/features/rh/api/employeeDocuments.service';
import { documentTypesService } from '@/features/configuracion/api/documentTypes.service';
import { employeesService } from '@/features/rh/api/employees.service';
import { fileService } from '@/services/fileService';
import type {
  EmployeeDocument,
  DocumentType,
  Employee,
  DocumentSummary,
  DocumentStatus,
} from '@/types/employee.types';
import {
  LoadingSpinner,
  DocumentStatusBadge,
  Modal,
  ProgressBar,
} from '@/shared/components';
import FilterSelect from '@/shared/components/FilterSelect';
import { ConfirmarEliminacion } from '@/shared/components/ConfirmarEliminacion';
import {
  Upload,
  FileText,
  CheckCircle,
  XCircle,
  Eye,
  Download,
  AlertTriangle,
  Search,
  RefreshCw,
  CircleQuestionMark,
  Trash2,
  Clock,
  Clock4,
  CircleX,
  CheckCheck,
  PencilLine,
} from "lucide-react";
import {
  usePermissions,
  EMPLOYEE_PERMISSIONS,
} from '@/shared/hooks/usePermissions';
import { Notification, useNotification } from '@/shared/components/Notification';
import SelectBuscable from '@/shared/components/SelectBuscable';
import { EsqueletoTabla } from "@/shared/components/Esqueleto";
import { formatDate, formatDateTimeMX } from '@/shared/lib/dateUtils'

interface EmployeeDigitalFileProps {
  showOptionalDocumentsOnly?: boolean;
  isEditable?: boolean;
  onEditToggle?: () => void;
}

interface DocumentRow {
  id?: number;
  documentTypeId: number;
  documentType: DocumentType;
  status: DocumentStatus | "sin_cargar";
  version?: number;
  fileUrl?: string;
  fileName?: string | null;
  createdAt?: string;
  fechaVencimiento?: string | null;
  comentarios?: string | null;
  isUploaded: boolean;
  isMissing: boolean;
}

export const EmployeeDigitalFile: React.FC<EmployeeDigitalFileProps> = ({
  showOptionalDocumentsOnly = false,
  isEditable = false,
  onEditToggle,
}) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { hasPermission, isLoading: isAuthLoading } = usePermissions();
  const { notification, showSuccess, showError, hideNotification } =
    useNotification();

  const [employee, setEmployee] = useState<Employee | null>(null);
  const [documents, setDocuments] = useState<EmployeeDocument[]>([]);
  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([]);
  const [summary, setSummary] = useState<DocumentSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [selectedStatus, setSelectedStatus] = useState<string>("");

  // Upload Modal
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadDocumentTypeId, setUploadDocumentTypeId] = useState<
    number | null
  >(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadExpirationDate, setUploadExpirationDate] = useState("");
  const [uploadObservations, setUploadObservations] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // Approve Modal
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approveDocument, setApproveDocument] =
    useState<EmployeeDocument | null>(null);
  const [approveObservations, setApproveObservations] = useState("");
  const [isApproving, setIsApproving] = useState(false);

  // Reject Modal
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectDocument, setRejectDocument] = useState<EmployeeDocument | null>(
    null,
  );
  const [rejectReason, setRejectReason] = useState("");
  const [isRejecting, setIsRejecting] = useState(false);

  // Versions Modal
  const [showVersionsModal, setShowVersionsModal] = useState(false);
  const [versionsDocument, setVersionsDocument] =
    useState<EmployeeDocument | null>(null);
  const [versions, setVersions] = useState<EmployeeDocument[]>([]);
  const [isLoadingVersions, setIsLoadingVersions] = useState(false);

  // Document Viewer Modal
  const [showViewerModal, setShowViewerModal] = useState(false);
  const [viewerDocument, setViewerDocument] = useState<EmployeeDocument | null>(
    null,
  );
  const [viewerFileUrl, setViewerFileUrl] = useState<string | null>(null);
  const [viewerFileError, setViewerFileError] = useState<string | null>(null);
  const [isViewerFileLoading, setIsViewerFileLoading] = useState(false);
  const [viewerAction, setViewerAction] = useState<
    "approve" | "reject" | "revision" | "expire" | null
  >(null);
  const [viewerObservations, setViewerObservations] = useState("");
  const [isProcessingAction, setIsProcessingAction] = useState(false);

  // Not Applicable Modal
  const [showNotApplicableModal, setShowNotApplicableModal] = useState(false);
  const [notApplicableDocumentType, setNotApplicableDocumentType] =
    useState<DocumentType | null>(null);
  const [notApplicableReason, setNotApplicableReason] = useState("");
  const [isMarkingNotApplicable, setIsMarkingNotApplicable] = useState(false);
  /** Documento cuyo borrado se está confirmando. */
  const [porEliminar, setPorEliminar] = useState<EmployeeDocument | null>(null);
  const [eliminando, setEliminando] = useState(false);

  const canApprove = hasPermission(
    EMPLOYEE_PERMISSIONS.EMPLOYEE_DOCUMENTS_APPROVE,
  );
  const canReject = hasPermission(
    EMPLOYEE_PERMISSIONS.EMPLOYEE_DOCUMENTS_DELETE,
  );

  const normalizeText = (value?: string | null): string =>
    (value || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();

  const isCvDocumentType = (documentType?: DocumentType | null): boolean => {
    const normalizedName = normalizeText(documentType?.nombre);
    return normalizedName.includes("curriculum") || normalizedName === "cv";
  };

  const isValidFileUrl = (fileUrl?: string | null): boolean => {
    if (typeof fileUrl !== "string") return false;
    const normalized = fileUrl.trim();
    if (!normalized) return false;

    const invalidTokens = new Set(["null", "undefined", "n/a", "na", "-"]);
    return !invalidTokens.has(normalized.toLowerCase());
  };

  const isDocumentRequired = (documentType?: DocumentType | null): boolean => {
    if (!documentType) return false;
    if (isCvDocumentType(documentType) && employee?.cvRequired === false) {
      return false;
    }
    return Boolean(documentType.esObligatorio);
  };

  const hasDocumentFile = (doc: DocumentRow): boolean =>
    isValidFileUrl(doc.fileUrl);

  const handleEliminar = async () => {
    if (!porEliminar?.id) return;
    try {
      setEliminando(true);
      await employeeDocumentsService.delete(porEliminar.id);
      setPorEliminar(null);
      showSuccess(
        "Documento eliminado",
        "El tipo de documento queda pendiente de carga.",
      );
      await loadData();
    } catch (errCrudo: unknown) {
      const err = errCrudo as { message?: string };
      showError(
        "No se pudo eliminar",
        err.message || "Intenta de nuevo en un momento.",
      );
    } finally {
      setEliminando(false);
    }
  };

  const isVisibleForCurrentTab = (documentType?: DocumentType | null): boolean => {
    if (!documentType) return false;
    return showOptionalDocumentsOnly
      ? !isDocumentRequired(documentType)
      : isDocumentRequired(documentType);
  };

  useEffect(() => {
    if (id) {
      loadData();
    }
  }, [id]);

  useEffect(() => {
    return () => {
      fileService.revokeViewableFileUrl(viewerFileUrl);
    };
  }, [viewerFileUrl]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [employeeData, documentsData, typesData, summaryData] =
        await Promise.all([
          employeesService.getById(Number(id)),
          employeeDocumentsService.getAll({ employeeId: Number(id) }),
          documentTypesService.getAll(),
          employeeDocumentsService.getSummary(Number(id)),
        ]);
      setEmployee(employeeData);
      setDocuments(documentsData);
      setDocumentTypes(typesData.filter((dt) => dt.activo));
      setSummary(summaryData);
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error("Error al cargar datos:", err);
      showError(
        "Error al cargar datos",
        err.message || "No se pudieron cargar los documentos del empleado",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setUploadFile(e.target.files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setUploadFile(e.dataTransfer.files[0]);
    }
  };

  const handleUploadSubmit = async () => {
    if (!uploadDocumentTypeId || !uploadFile) {
      showError(
        "Datos incompletos",
        "Por favor selecciona un tipo de documento y un archivo",
      );
      return;
    }

    try {
      setIsUploading(true);
      await employeeDocumentsService.upload(uploadFile, {
        employeeId: Number(id),
        documentTypeId: uploadDocumentTypeId,
        comentarios: uploadObservations || undefined,
        fechaVencimiento: uploadExpirationDate || undefined,
      });
      await loadData();

      // Cerrar modal y limpiar antes de mostrar éxito
      setShowUploadModal(false);
      setUploadDocumentTypeId(null);
      setUploadFile(null);
      setUploadExpirationDate("");
      setUploadObservations("");

      showSuccess(
        "Documento cargado",
        "El documento se ha cargado correctamente",
      );
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error("Error al cargar documento:", err);

      // Cerrar modal primero para que la notificación sea visible
      setShowUploadModal(false);

      // Procesar mensaje de error (puede ser string o array)
      let errorMessage = "No se pudo cargar el documento";
      if (err.message) {
        if (Array.isArray(err.message)) {
          // Traducir mensajes comunes del backend
          errorMessage = err.message
            .map((msg: string) => {
              if (msg.includes("employeeId must be an integer")) {
                return "El ID del empleado debe ser un número entero";
              }
              if (msg.includes("documentTypeId must be an integer")) {
                return "El ID del tipo de documento debe ser un número entero";
              }
              return msg;
            })
            .join(", ");
        } else {
          errorMessage = err.message;
        }
      }

      showError("Error al cargar documento", errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  const handleApproveClick = (doc: EmployeeDocument) => {
    setApproveDocument(doc);
    setApproveObservations("");
    setShowApproveModal(true);
  };

  const handleApproveSubmit = async () => {
    if (!approveDocument) return;
    try {
      setIsApproving(true);
      await employeeDocumentsService.approve(
        approveDocument.id,
        approveObservations,
      );
      await loadData();
      setShowApproveModal(false);
      setApproveDocument(null);
      setApproveObservations("");
      showSuccess(
        "Documento aprobado",
        "El documento ha sido aprobado correctamente",
      );
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error("Error al aprobar documento:", err);
      showError(
        "Error al aprobar documento",
        err.message || "No se pudo aprobar el documento",
      );
    } finally {
      setIsApproving(false);
    }
  };

  const handleRejectClick = (doc: EmployeeDocument) => {
    setRejectDocument(doc);
    setRejectReason("");
    setShowRejectModal(true);
  };

  const handleRejectSubmit = async () => {
    if (!rejectDocument || !rejectReason.trim()) {
      showError("Motivo requerido", "Debes proporcionar un motivo de rechazo");
      return;
    }
    try {
      setIsRejecting(true);
      await employeeDocumentsService.reject(rejectDocument.id, rejectReason);
      await loadData();
      setShowRejectModal(false);
      setRejectDocument(null);
      setRejectReason("");
      showSuccess(
        "Documento rechazado",
        "El documento ha sido rechazado correctamente",
      );
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error("Error al rechazar documento:", err);
      showError(
        "Error al rechazar documento",
        err.message || "No se pudo rechazar el documento",
      );
    } finally {
      setIsRejecting(false);
    }
  };

  const handleViewVersions = async (doc: EmployeeDocument) => {
    if (!doc.documentTypeId) return;
    try {
      setVersionsDocument(doc);
      setShowVersionsModal(true);
      setIsLoadingVersions(true);
      const versionsData = await employeeDocumentsService.getVersions(
        Number(id),
        doc.documentTypeId,
      );
      setVersions(versionsData);
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error("Error al cargar versiones:", err);
      showError(
        "Error al cargar versiones",
        err.message || "No se pudieron cargar las versiones del documento",
      );
    } finally {
      setIsLoadingVersions(false);
    }
  };

  const closeViewerModal = () => {
    setShowViewerModal(false);
    setViewerDocument(null);
    setViewerAction(null);
    setViewerObservations("");
    setViewerFileError(null);
    setIsViewerFileLoading(false);
    fileService.revokeViewableFileUrl(viewerFileUrl);
    setViewerFileUrl(null);
  };

  const handleOpenViewer = async (doc: EmployeeDocument) => {
    setViewerDocument(doc);
    setViewerAction(null);
    setViewerObservations("");
    setViewerFileError(null);
    setIsViewerFileLoading(true);
    fileService.revokeViewableFileUrl(viewerFileUrl);
    setViewerFileUrl(null);
    setShowViewerModal(true);

    if (!isValidFileUrl(doc.fileUrl)) {
      setViewerFileError("No hay archivo disponible para este documento.");
      setIsViewerFileLoading(false);
      return;
    }

    try {
      const blobUrl = await fileService.getViewableFileUrl(doc.fileUrl);
      setViewerFileUrl(blobUrl);
    } catch (err) {
      console.error("Error al cargar visor de documento:", err);
      setViewerFileError(
        "No fue posible visualizar el documento en este momento. Verifica permisos o intenta más tarde.",
      );
    } finally {
      setIsViewerFileLoading(false);
    }
  };

  const handleViewerActionSubmit = async () => {
    if (!viewerDocument || !viewerAction) return;

    try {
      setIsProcessingAction(true);

      switch (viewerAction) {
        case "approve":
          await employeeDocumentsService.approve(
            viewerDocument.id,
            viewerObservations,
          );
          showSuccess(
            "Documento aprobado",
            "El documento ha sido aprobado correctamente",
          );
          break;
        case "reject":
          if (!viewerObservations.trim()) {
            showError(
              "Motivo requerido",
              "Debes proporcionar un motivo de rechazo",
            );
            return;
          }
          await employeeDocumentsService.reject(
            viewerDocument.id,
            viewerObservations,
          );
          showSuccess(
            "Documento rechazado",
            "El documento ha sido rechazado correctamente",
          );
          break;
        case "revision":
          await employeeDocumentsService.sendToReview(
            viewerDocument.id,
            viewerObservations,
          );
          showSuccess(
            "Enviado a revisión",
            "El documento ha sido enviado a revisión",
          );
          break;
        case "expire":
          await employeeDocumentsService.markAsExpired(
            viewerDocument.id,
            viewerObservations,
          );
          showSuccess(
            "Documento vencido",
            "El documento ha sido marcado como vencido",
          );
          break;
      }

      await loadData();
      closeViewerModal();
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error("Error al procesar acción:", err);
      showError("Error", err.message || "No se pudo procesar la acción");
    } finally {
      setIsProcessingAction(false);
    }
  };

  const handleDownload = async (doc: EmployeeDocument) => {
    try {
      if (!isValidFileUrl(doc.fileUrl)) {
        showError(
          "Archivo no disponible",
          "Este documento no tiene un archivo asociado",
        );
        return;
      }

      await fileService.downloadFile(
        doc.fileUrl,
        `${doc.documentType?.nombre || "documento"}_${doc.version || "v1"}`,
      );

      showSuccess("Descarga iniciada", "El documento se está descargando");
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error("Error al descargar documento:", err);
      showError(
        "Error al descargar",
        err.message || "No se pudo descargar el documento",
      );
    }
  };

  const handleOpenNotApplicable = (docType: DocumentType) => {
    setNotApplicableDocumentType(docType);
    setNotApplicableReason("");
    setShowNotApplicableModal(true);
  };

  const handleMarkAsNotApplicable = async () => {
    if (!notApplicableDocumentType || !id) return;

    try {
      setIsMarkingNotApplicable(true);
      await employeeDocumentsService.markAsNotApplicable(
        Number(id),
        notApplicableDocumentType.id,
        notApplicableReason,
      );
      showSuccess(
        "Marcado como No Aplica",
        `El documento "${notApplicableDocumentType.nombre}" ha sido marcado como no aplicable`,
      );
      await loadData();
      setShowNotApplicableModal(false);
      setNotApplicableDocumentType(null);
      setNotApplicableReason("");
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error("Error al marcar como no aplica:", err);
      showError(
        "Error",
        err.message || "No se pudo marcar el documento como no aplicable",
      );
    } finally {
      setIsMarkingNotApplicable(false);
    }
  };

  const handleView = (doc: EmployeeDocument) => {
    handleOpenViewer(doc);
  };

  /**
   * Combina tipos de documentos con documentos cargados
   */
  const getAllDocumentRows = (): DocumentRow[] => {
    const rows: DocumentRow[] = [];

    documentTypes.filter(isVisibleForCurrentTab).forEach((docType) => {
      const uploadedDoc = documents.find(
        (doc) => doc.documentTypeId === docType.id,
      );

      if (uploadedDoc) {
        rows.push({
          ...uploadedDoc,
          documentType: docType,
          isUploaded: true,
          isMissing: false,
        } as DocumentRow);
      } else {
        rows.push({
          documentTypeId: docType.id,
          documentType: docType,
          status: "sin_cargar",
          isUploaded: false,
          isMissing: isDocumentRequired(docType),
        });
      }
    });

    return rows;
  };

  const getFilteredDocuments = (): DocumentRow[] => {
    let filtered = getAllDocumentRows();

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (doc: DocumentRow) =>
          doc.documentType?.nombre.toLowerCase().includes(term) ||
          doc.documentType?.categoria.toLowerCase().includes(term) ||
          doc.comentarios?.toLowerCase().includes(term),
      );
    }

    // Filter by category
    if (selectedCategory) {
      filtered = filtered.filter(
        (doc: DocumentRow) => doc.documentType?.categoria === selectedCategory,
      );
    }

    // Filter by status
    if (selectedStatus) {
      filtered = filtered.filter(
        (doc: DocumentRow) => doc.status === selectedStatus,
      );
    }

    return filtered;
  };

  const getCategories = (): string[] => {
    const categories = new Set(
      documentTypes
        .filter(isVisibleForCurrentTab)
        .map((dt) => dt.categoria),
    );
    return Array.from(categories).sort();
  };

  /**
   * Obtener tipos de documentos que no tienen documento cargado
   */
  const getMissingDocumentTypes = (): DocumentType[] => {
    return documentTypes.filter((docType) => {
      if (!isVisibleForCurrentTab(docType)) return false;
      // Verificar si NO existe un documento para este tipo
      const hasDocument = documents.some(
        (doc) => doc.documentTypeId === docType.id,
      );
      return !hasDocument;
    });
  };

  /**
   * Obtener tipos de documentos disponibles para subir (excluyendo los que ya tienen documentos)
   */
  /**
   * Tipos que todavía se pueden cargar.
   *
   * El descarte va por archivo utilizable, no por existir el registro. Antes
   * bastaba con que hubiera una fila del documento —aunque viniera sin archivo
   * o rechazada— para que el tipo desapareciera del desplegable, mientras la
   * tabla lo seguía mostrando como «sin cargar». Al abrir el modal desde ese
   * renglón, el select no encontraba su opción y se quedaba en blanco.
   */
  const getAvailableDocumentTypes = (): DocumentType[] => {
    return documentTypes.filter((docType) => {
      if (!isVisibleForCurrentTab(docType)) return false;
      // El preseleccionado siempre está, venga de donde venga.
      if (docType.id === uploadDocumentTypeId) return true;

      const yaTieneArchivo = documents.some(
        (doc) =>
          doc.documentTypeId === docType.id &&
          doc.status !== "no_aplica" &&
          isValidFileUrl(doc.fileUrl),
      );
      return !yaTieneArchivo;
    });
  };

  const filteredDocuments = getFilteredDocuments();

  const getCategoryName = (category: string): string => {
    const categoryMap: { [key: string]: string } = {
      condicional_rh: "Condicional RH",
      ehs_cliente: "EHS Cliente",
    };
    if (categoryMap[category]) return categoryMap[category];
    const normalized = category.replace(/_/g, " ").trim().toLowerCase();
    if (!normalized) return category;
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  };

  if (isLoading || isAuthLoading) {
    return (
      <div className="bg-white rounded-lg border border-[#E5E7EB] overflow-hidden">
        <EsqueletoTabla filas={7} columnas={4} />
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="w-full max-w-none px-4 sm:px-6 lg:px-8">
          <div className="bg-red-50 border-l-4 border-red-500 rounded-r-lg p-6 shadow-sm">
            <h3 className="text-lg font-bold text-red-700 mb-2">
              Error al cargar empleado
            </h3>
            <button
              type="button"
              onClick={() => navigate("/employees")}
              className={estiloDeBoton("primario")}
            >
              Volver a la lista
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (showOptionalDocumentsOnly) {
    return (
      <>
        <div className="min-h-[400px] h-auto bg-[#ffffff] py-8 rounded-xl border border-[#E5E7EB]">
          <div className="w-full">
            <div className="flex items-center justify-between mb-4 border-b border-gray-200 px-6 pb-5">
              <h2 className="text-base font-semibold text-[#1E1E1F] flex items-center">
                Documentos opcionales
              </h2>
              {onEditToggle && (
                isEditable ? (
                  <button
                    type="button"
                    onClick={onEditToggle}
                    className={estiloDeBoton("secundario")}
                  >
                    Cancelar
                  </button>
                ) : (
                  <div
                    onClick={onEditToggle}
                    className="inline-flex items-center text-[#315FB9] cursor-pointer"
                  >
                    <PencilLine className="w-[24px] h-[24px]" />
                  </div>
                )
              )}
            </div>
            {/* Missing Optional Documents Info */}
            {getMissingDocumentTypes().length > 0 && (
              <div className="mb-6 bg-[#ffffff] p-6">
                <div className="flex items-start gap-3">
                  <div className="flex-1">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {getMissingDocumentTypes().map((docType) => (
                        <div
                          key={docType.id}
                          className="flex items-center justify-between p-3 bg-[#FAFBFC] rounded-lg hover:bg-gray-50 cursor-pointer transition-colors border border-transparent hover:border-blue-200"
                          onClick={() => {
                            if (!isEditable) return;
                            setUploadDocumentTypeId(docType.id);
                            setShowUploadModal(true);
                          }}
                        >
                          <div className="flex-1 min-w-0 mr-3">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {docType.nombre}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {docType.categoria
                                ? getCategoryName(docType.categoria)
                                : "Sin categoría"}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            {/* Upload indicator icon */}
                            <Upload className="w-4 h-4 text-gray-400 hover:text-blue-500" />

                            {isEditable &&
                              hasPermission(
                              EMPLOYEE_PERMISSIONS.EMPLOYEE_DOCUMENTS_CREATE,
                            ) && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation(); // Prevent opening upload modal
                                  handleOpenNotApplicable(docType);
                                }}
                                className={estiloDeBoton("secundario", "z-10 flex-shrink-0")}
                                title="Marcar como No Aplica"
                              >
                                N/A
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Upload Modal */}
        <Modal
          isOpen={showUploadModal}
          onClose={() => {
            setShowUploadModal(false);
            setUploadDocumentTypeId(null);
            setUploadFile(null);
            setUploadExpirationDate("");
            setUploadObservations("");
          }}
          title="Cargar Documento"
          size="lg"
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Tipo de Documento *
              </label>
              <SelectBuscable
                variante="formulario"
                opciones={getAvailableDocumentTypes().map((dt) => ({
                  value: String(dt.id),
                  label: `${dt.nombre} ${isDocumentRequired(dt) ? "(Obligatorio)" : ""}`.trim(),
                }))}
                value={uploadDocumentTypeId ? String(uploadDocumentTypeId) : ""}
                onChange={(valor) => setUploadDocumentTypeId(valor === "" ? null : Number(valor))}
                placeholder="Seleccionar tipo..."
                placeholderDeBusqueda="Buscar tipo de documento..."
                />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Archivo *
              </label>
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  isDragging
                    ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                    : "border-gray-300 dark:border-gray-600"
                }`}
              >
                <Upload className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                {uploadFile ? (
                  <div>
                    <p className="text-sm text-gray-900 dark:text-white font-medium mb-1">
                      {uploadFile.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {(uploadFile.size / 1024).toFixed(2)} KB
                    </p>
                    <button
                      type="button"
                      onClick={() => setUploadFile(null)}
                      className="mt-2 text-sm text-red-600 hover:text-red-700"
                    >
                      Eliminar
                    </button>
                  </div>
                ) : (
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                      Arrastra un archivo aquí o haz clic para seleccionar
                    </p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      onChange={handleFileSelect}
                      className="hidden"
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className={estiloDeBoton("primario")}
                    >
                      Seleccionar archivo
                    </button>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      PDF, Word o imagen (Máx. 10MB)
                    </p>
                  </div>
                )}
              </div>
            </div>

            {uploadDocumentTypeId &&
              documentTypes.find((dt) => dt.id === uploadDocumentTypeId)
                ?.tieneVencimiento && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Fecha de Vencimiento
                  </label>
                  <input
                    type="date"
                    value={uploadExpirationDate}
                    onChange={(e) => setUploadExpirationDate(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Observaciones
              </label>
              <textarea
                value={uploadObservations}
                onChange={(e) => setUploadObservations(e.target.value)}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                placeholder="Notas adicionales sobre el documento..."
              />
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setShowUploadModal(false)}
              className={estiloDeBoton("secundario", "flex-1")}
              disabled={isUploading}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleUploadSubmit}
              disabled={isUploading || !uploadDocumentTypeId || !uploadFile}
              className={estiloDeBoton("primario")}
            >
              {isUploading && <LoadingSpinner size="sm" />}
              Cargar Documento
            </button>
          </div>
        </Modal>

        {/* Not Applicable Modal */}
        <Modal
          isOpen={showNotApplicableModal}
          onClose={() => {
            setShowNotApplicableModal(false);
            setNotApplicableDocumentType(null);
            setNotApplicableReason("");
          }}
          title="Marcar Documento como No Aplicable"
          size="lg"
        >
          <div className="space-y-4">
            <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-4 rounded-r-lg">
              <div className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-blue-700 dark:text-blue-400 font-medium">
                    {notApplicableDocumentType?.nombre}
                  </p>
                  <p className="text-xs text-blue-600 dark:text-blue-500 mt-1">
                    {notApplicableDocumentType?.categoria
                      ? getCategoryName(notApplicableDocumentType.categoria)
                      : "Sin categoría"}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Al marcar este documento como <strong>"No Aplica"</strong>, el
                sistema lo registrará sin necesidad de archivo. Esta acción es
                útil cuando el documento no es requerido para este empleado en
                particular.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Motivo (opcional)
              </label>
              <textarea
                value={notApplicableReason}
                onChange={(e) => setNotApplicableReason(e.target.value)}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                placeholder="Ej: El empleado no requiere este documento por su tipo de contrato..."
              />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={() => {
                  setShowNotApplicableModal(false);
                  setNotApplicableDocumentType(null);
                  setNotApplicableReason("");
                }}
                className={estiloDeBoton("secundario", "flex-1")}
                disabled={isMarkingNotApplicable}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleMarkAsNotApplicable}
                className={estiloDeBoton("primario")}
                disabled={isMarkingNotApplicable}
              >
                {isMarkingNotApplicable ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Marcando...
                  </>
                ) : (
                  "Marcar como N/A"
                )}
              </button>
            </div>
          </div>
        </Modal>
      </>
    );
  }
  return (
    <div className="min-h-screen bg-[#ffffff] py-8 rounded-xl border border-[#E5E7EB]">
      <div className="w-full">
        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5 mb-6 px-6">
            <div className="bg-[#ffffff] rounded-xl border border-[#E5E7EB] p-4 max-w-full max-h-[108px]">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Progreso
                </span>
              </div>
              <p className="text-[#1F2937] text-2xl font-semibold mb-1">
                {employee.completenessPercentage}%
              </p>
              <ProgressBar
                value={employee.completenessPercentage}
                size={"md"}
                showLabel={false}
              />
            </div>

            <div className="bg-[#ffffff] rounded-xl border border-[#E5E7EB] p-4 max-w-full max-h-[108px]">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Total
                </span>
              </div>
              <p className="text-[#1F2937] text-2xl font-semibold mt-6">
                {summary.total}
              </p>
            </div>

            <div className="bg-[#ffffff] rounded-xl border border-[#E5E7EB] p-4 max-w-full max-h-[108px]">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Aprobados
                </span>
                <CheckCircle className="w-[24px] h-[24px] text-[#2FD599]" />
              </div>
              <p className="text-[#1F2937] text-2xl font-semibold mt-6">
                {summary.aprobados}
              </p>
            </div>

            <div className="bg-[#ffffff] rounded-xl border border-[#E5E7EB] p-4 max-w-full max-h-[108px]">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  En revisión
                </span>
                <Clock4 className="w-[24px] h-[24px] text-[#EFB81E]" />
              </div>
              <p className="text-[#1F2937] text-2xl font-semibold mt-6">
                {summary.pendientes + summary.enRevision}
              </p>
            </div>

            <div className="bg-[#ffffff] rounded-xl border border-[#E5E7EB] p-4 max-w-full max-h-[108px]">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Rechazados/
                  <br /> vencidos
                </span>
                <CircleX className="w-[24px] h-[24px] text-[#EF4444] mt-[-15px]" />
              </div>
              <p className="text-[#1F2937] text-2xl font-semibold mt-3">
                {summary.rechazados + summary.vencidos}
              </p>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="border-t border-1 border-[#E0E0E0] p-6 w-full">
          {/* Filters */}
          <div className="w-full flex flex-wrap items-center gap-3 mb-8">
            {/* La leyenda de los obligatorios vive aquí y no en cada renglón:
                en la tabla eran 14 repeticiones del mismo aviso. */}
            <div className="relative group flex-shrink-0">
              <button
                type="button"
                aria-label="Qué significa el asterisco"
                className="flex items-center text-[#D93E3E] transition-opacity hover:opacity-80"
              >
                <CircleQuestionMark className="w-5 h-5" />
              </button>
              <span className="absolute left-0 top-full mt-1 hidden group-hover:block whitespace-nowrap rounded-md bg-[#F8E3E3] px-2.5 py-1.5 text-xs font-medium text-[#832525] shadow-sm z-50">
                Los documentos marcados con * son obligatorios
              </span>
            </div>

            <div className="relative w-full max-w-[320px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar en expediente..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:border-[#315FB9]"
              />
            </div>

            <FilterSelect
              label="Categoría"
              ancho="contenido"
              options={getCategories().map((cat) => ({
                value: cat,
                label: getCategoryName(cat),
              }))}
              value={selectedCategory}
              onChange={setSelectedCategory}
            />

            <FilterSelect
              label="Estado"
              ancho="contenido"
              options={[
                { value: "sin_cargar", label: "Sin cargar" },
                { value: "pendiente", label: "Pendiente" },
                { value: "en_revision", label: "En revisión" },
                { value: "aprobado", label: "Aprobado" },
                { value: "rechazado", label: "Rechazado" },
                { value: "vencido", label: "Vencido" },
                { value: "no_aplica", label: "No aplica" },
              ]}
              value={selectedStatus}
              onChange={setSelectedStatus}
            />

            <button
              type="button"
              onClick={loadData}
              className={estiloDeBoton("secundario", "ml-auto")}
            >
              <RefreshCw className="w-4 h-4" />
              Actualizar
            </button>
          </div>

          {/* Documents Table */}
          <div className="bg-white rounded-lg overflow-hidden border border-[#E5E7EB]">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-[#E5E7EB]">
              <thead className="bg-[#FAF9FF]">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                    Nombre
                  </th>

                  <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                    Documento
                  </th>

                  <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                    Versión
                  </th>
                  <th className="w-[180px] px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                    Fecha de Carga
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                    Vencimiento
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-[#E1E1E1]">
                {filteredDocuments.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-6 py-12 text-center text-gray-500 dark:text-gray-400"
                    >
                      <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No se encontraron documentos</p>
                    </td>
                  </tr>
                ) : (
                  filteredDocuments.map((doc) => {
                    const hasFile = hasDocumentFile(doc);
                    const isCvWithoutFile =
                      isCvDocumentType(doc.documentType) && !hasFile;
                    return (
                      <tr
                        key={doc.id || `missing-${doc.documentTypeId}`}
                        className="hover:bg-[#F4F7FB] transition-colors"
                      >
                        {/* Nombre del tipo de documento. El asterisco solo: la
                            leyenda de qué significa vive en el tooltip de los
                            filtros, no repetida en cada renglón. */}
                        <td className="px-6 py-4">
                          <p className="text-sm font-medium text-gray-900">
                            {doc.documentType?.nombre || "Documento"}
                            {isDocumentRequired(doc.documentType) && (
                              <span className="text-red-600" aria-label="Obligatorio">
                                *
                              </span>
                            )}
                          </p>
                          {doc.comentarios && (
                            <p className="text-xs text-gray-500 mt-1">
                              {doc.comentarios}
                            </p>
                          )}
                          {isCvWithoutFile && (
                            <p className="text-xs text-amber-600 mt-1">
                              Sin archivo disponible
                            </p>
                          )}
                        </td>

                        {/* El archivo cargado, cuando lo hay. */}
                        <td className="px-6 py-4">
                          {hasFile ? (
                            <button
                              type="button"
                              onClick={() =>
                                handleView(doc as unknown as EmployeeDocument)
                              }
                              className="text-sm text-[#315FB9] hover:underline text-left"
                            >
                              {doc.fileName ||
                                `${doc.documentType?.nombre || "Documento"}.pdf`}
                            </button>
                          ) : (
                            <span className="text-sm text-gray-400">
                              {doc.status === "no_aplica" ? "N/A" : "------"}
                            </span>
                          )}
                        </td>

                        <td
                          className={`px-6 py-4`}
                        
                        >
                          <DocumentStatusBadge
                            status={doc.status as DocumentStatus}
                            onAction={
                              doc.isUploaded && hasFile
                                ? () =>
                                    handleView(
                                      doc as unknown as EmployeeDocument,
                                    )
                                : undefined
                            }
                          />
                        </td>
                        <td
                          className="px-6 py-4 text-sm"
                        >
                          {hasFile ? (
                            <button
                              type="button"
                              onClick={() =>
                                handleViewVersions(
                                  doc as unknown as EmployeeDocument,
                                )
                              }
                              className="text-sm font-medium text-[#315FB9] hover:underline"
                              title="Ver historial de versiones"
                            >
                              {doc.version ? `V${doc.version}` : "V1"}
                            </button>
                          ) : (
                            <span className="text-sm text-gray-400">-----</span>
                          )}
                        </td>
                        <td
                          className={`px-6 py-4 text-sm text-gray-600 dark:text-gray-400 ${doc.isUploaded && hasFile ? "cursor-pointer" : ""}`}
                          onClick={() =>
                            doc.isUploaded &&
                            hasFile &&
                            handleView(doc as unknown as EmployeeDocument)
                          }
                        >
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {doc.createdAt
                              ? formatDate(doc.createdAt)
                              : "-----"}
                          </p>
                        </td>
                        <td
                          className={`px-6 py-4 text-sm text-gray-600 dark:text-gray-400 ${doc.isUploaded && hasFile ? "cursor-pointer" : ""}`}
                          onClick={() =>
                            doc.isUploaded &&
                            hasFile &&
                            handleView(doc as unknown as EmployeeDocument)
                          }
                        >
                          {hasFile && doc.fechaVencimiento ? (
                            <span
                              className={
                                doc.status === "vencido"
                                  ? "text-red-600 dark:text-red-400 font-medium"
                                  : "text-sm font-medium text-gray-900 dark:text-white"
                              }
                            >
                              {formatDate(doc.fechaVencimiento)}
                            </span>
                          ) : (
                            <span className="text-gray-400">-----</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            {!hasFile
                              ? isEditable &&
                                hasPermission(
                                  EMPLOYEE_PERMISSIONS.EMPLOYEE_DOCUMENTS_CREATE,
                                ) && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setUploadDocumentTypeId(
                                        doc.documentTypeId,
                                      );
                                      setShowUploadModal(true);
                                    }}
                                    className="inline-flex items-center gap-1.5 text-sm font-medium text-[#315FB9] hover:underline"
                                  >
                                    <Upload className="w-4 h-4" />
                                    Cargar
                                  </button>
                                )
                              : (
                                <>
                                  {isEditable &&
                                    hasPermission(
                                      EMPLOYEE_PERMISSIONS.EMPLOYEE_DOCUMENTS_CREATE,
                                    ) && (
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setUploadDocumentTypeId(
                                            doc.documentTypeId,
                                          );
                                          setShowUploadModal(true);
                                        }}
                                        className="p-1 text-[#315FB9] hover:text-blue-700"
                                        title="Reemplazar documento"
                                      >
                                        <PencilLine className="w-5 h-5" />
                                      </button>
                                    )}
                                  {isEditable &&
                                    hasPermission(
                                      EMPLOYEE_PERMISSIONS.EMPLOYEE_DOCUMENTS_DELETE,
                                    ) &&
                                    doc.id && (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          setPorEliminar(
                                            doc as unknown as EmployeeDocument,
                                          )
                                        }
                                        className="p-1 text-red-600 hover:text-red-700"
                                        title="Eliminar documento"
                                      >
                                        <Trash2 className="w-5 h-5" />
                                      </button>
                                    )}
                                  {/* La revisión sigue viva: sin estos dos, un
                                      documento en revisión se queda ahí para
                                      siempre. */}
                                  {canApprove && doc.status === "en_revision" && (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleApproveClick(
                                          doc as unknown as EmployeeDocument,
                                        )
                                      }
                                      className="p-1 text-[#315FB9] hover:text-green-700"
                                      title="Aprobar"
                                    >
                                      <CheckCircle className="w-5 h-5" />
                                    </button>
                                  )}
                                  {canReject && doc.status === "en_revision" && (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleRejectClick(
                                          doc as unknown as EmployeeDocument,
                                        )
                                      }
                                      className="p-1 text-red-600 hover:text-red-700"
                                      title="Rechazar"
                                    >
                                      <XCircle className="w-5 h-5" />
                                    </button>
                                  )}
                                </>
                              )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          </div>
        </div>
      </div>

      {/* Upload Modal */}
      <Modal
        isOpen={showUploadModal}
        onClose={() => {
          setShowUploadModal(false);
          setUploadDocumentTypeId(null);
          setUploadFile(null);
          setUploadExpirationDate("");
          setUploadObservations("");
        }}
        title="Cargar Documento"
        size="lg"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Tipo de Documento *
            </label>
            <SelectBuscable
              variante="formulario"
              opciones={getAvailableDocumentTypes().map((dt) => ({
                value: String(dt.id),
                label: `${dt.nombre} ${isDocumentRequired(dt) ? "(Obligatorio)" : ""}`.trim(),
              }))}
              value={uploadDocumentTypeId ? String(uploadDocumentTypeId) : ""}
              onChange={(valor) => setUploadDocumentTypeId(valor === "" ? null : Number(valor))}
              placeholder="Seleccionar tipo..."
              placeholderDeBusqueda="Buscar tipo de documento..."
              />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Archivo *
            </label>
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                isDragging
                  ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                  : "border-gray-300 dark:border-gray-600"
              }`}
            >
              <Upload className="w-12 h-12 mx-auto mb-3 text-gray-400" />
              {uploadFile ? (
                <div>
                  <p className="text-sm text-gray-900 dark:text-white font-medium mb-1">
                    {uploadFile.name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {(uploadFile.size / 1024).toFixed(2)} KB
                  </p>
                  <button
                    type="button"
                    onClick={() => setUploadFile(null)}
                    className="mt-2 text-sm text-red-600 hover:text-red-700"
                  >
                    Eliminar
                  </button>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    Arrastra un archivo aquí o haz clic para seleccionar
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileSelect}
                    className="hidden"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className={estiloDeBoton("primario")}
                  >
                    Seleccionar archivo
                  </button>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    PDF, Word o imagen (Máx. 10MB)
                  </p>
                </div>
              )}
            </div>
          </div>

          {uploadDocumentTypeId &&
            documentTypes.find((dt) => dt.id === uploadDocumentTypeId)
              ?.tieneVencimiento && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Fecha de Vencimiento
                </label>
                <input
                  type="date"
                  value={uploadExpirationDate}
                  onChange={(e) => setUploadExpirationDate(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>
            )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Observaciones
            </label>
            <textarea
              value={uploadObservations}
              onChange={(e) => setUploadObservations(e.target.value)}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              placeholder="Notas adicionales sobre el documento..."
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={() => setShowUploadModal(false)}
            className={estiloDeBoton("secundario", "flex-1")}
            disabled={isUploading}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleUploadSubmit}
            disabled={isUploading || !uploadDocumentTypeId || !uploadFile}
            className={estiloDeBoton("primario")}
          >
            {isUploading && <LoadingSpinner size="sm" />}
            Cargar Documento
          </button>
        </div>
      </Modal>

      {/* Approve Modal */}
      <Modal
        isOpen={showApproveModal}
        onClose={() => setShowApproveModal(false)}
        title="Aprobar Documento"
      >
        {approveDocument && (
          <div className="space-y-4">
            <div className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                Documento
              </p>
              <p className="font-medium text-gray-900 dark:text-white">
                {approveDocument.documentType?.nombre || "Documento"}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Observaciones (opcional)
              </label>
              <textarea
                value={approveObservations}
                onChange={(e) => setApproveObservations(e.target.value)}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                placeholder="Notas adicionales sobre la aprobación..."
              />
            </div>
          </div>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={() => setShowApproveModal(false)}
            className={estiloDeBoton("secundario", "flex-1")}
            disabled={isApproving}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleApproveSubmit}
            disabled={isApproving}
            className={estiloDeBoton("exito")}
          >
            {isApproving && <LoadingSpinner size="sm" />}
            <CheckCircle className="w-4 h-4" />
            Aprobar Documento
          </button>
        </div>
      </Modal>

      {/* Reject Modal */}
      <Modal
        isOpen={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        title="Rechazar Documento"
      >
        {rejectDocument && (
          <div className="space-y-4">
            <div className="bg-red-50 border-l-4 border-red-500 rounded-r-lg p-4 shadow-sm">
              <p className="text-sm text-red-600 font-semibold mb-1">
                Documento
              </p>
              <p className="font-bold text-red-700">
                {rejectDocument.documentType?.nombre || "Documento"}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Motivo de Rechazo *
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                placeholder="Explica por qué se rechaza este documento..."
              />
            </div>
          </div>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={() => setShowRejectModal(false)}
            className={estiloDeBoton("secundario", "flex-1")}
            disabled={isRejecting}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleRejectSubmit}
            disabled={isRejecting || !rejectReason.trim()}
            className={estiloDeBoton("peligro")}
          >
            {isRejecting && <LoadingSpinner size="sm" />}
            <XCircle className="w-4 h-4" />
            Rechazar Documento
          </button>
        </div>
      </Modal>

      {/* Versions Modal */}
      <Modal
        isOpen={showVersionsModal}
        onClose={() => setShowVersionsModal(false)}
        title="Historial de Versiones"
        size="lg"
      >
        {versionsDocument && (
          <div className="space-y-4">
            <div className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                Documento
              </p>
              <p className="font-medium text-gray-900 dark:text-white">
                {versionsDocument.documentType?.nombre || "Documento"}
              </p>
            </div>

            {isLoadingVersions ? (
              <div className="py-8 flex justify-center">
                <LoadingSpinner size="md" text="Cargando versiones..." />
              </div>
            ) : versions.length === 0 ? (
              <p className="text-center text-gray-500 dark:text-gray-400 py-8">
                No hay versiones anteriores
              </p>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {versions.map((version) => (
                  <div
                    key={version.id}
                    className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          Versión {version.version}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {formatDateTimeMX(version.createdAt)}
                        </p>
                      </div>
                      <DocumentStatusBadge status={version.status} />
                    </div>
                    {version.comentarios && (
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                        {version.comentarios}
                      </p>
                    )}
                    {/* Solo mostrar botones Ver/Descargar si NO es "no_aplica" */}
                    {version.status !== "no_aplica" &&
                      isValidFileUrl(version.fileUrl) && (
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => handleView(version)}
                          className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                        >
                          <Eye className="w-3 h-3" />
                          Ver
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDownload(version)}
                          className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                        >
                          <Download className="w-3 h-3" />
                          Descargar
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={() => setShowVersionsModal(false)}
            className={estiloDeBoton("secundario", "flex-1")}
          >
            Cerrar
          </button>
        </div>
      </Modal>

      {/* Document Viewer Modal */}
      <Modal
        isOpen={showViewerModal}
        onClose={closeViewerModal}
        title="Visor de Documento"
        size="4xl"
        showBorderTitle={false}
      >
        {viewerDocument && (
          <div className="space-y-6">
            {/* Document Info */}
            <div className="p-4 bg-gray-50 dark:bg-gray-900 rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {viewerDocument.documentType?.nombre || "Documento"}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Versión {viewerDocument.version || "v1"} •{" "}
                    {viewerDocument.documentType?.categoria
                      ? getCategoryName(viewerDocument.documentType.categoria)
                      : "Sin categoría"}
                  </p>
                </div>
                <DocumentStatusBadge status={viewerDocument.status} />
              </div>
            </div>

            {/* PDF Viewer */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              {isViewerFileLoading ? (
                <div className="w-full h-96 flex items-center justify-center">
                  <LoadingSpinner text="Cargando documento..." />
                </div>
              ) : viewerFileError ? (
                <div className="w-full h-96 flex items-center justify-center px-6 text-center">
                  <div>
                    <p className="text-sm font-medium text-red-600">
                      {viewerFileError}
                    </p>
                    <p className="text-xs text-gray-500 mt-2">
                      Si el problema persiste, valida permisos de acceso al
                      documento.
                    </p>
                  </div>
                </div>
              ) : viewerFileUrl ? (
                <iframe
                  src={viewerFileUrl}
                  className="w-full h-96"
                  title="Visor de PDF"
                />
              ) : (
                <div className="w-full h-96 flex items-center justify-center px-6 text-center">
                  <p className="text-sm text-gray-500">
                    No hay archivo disponible para visualizar.
                  </p>
                </div>
              )}
            </div>

            {/* Action Selection */}
            {!viewerAction && (canApprove || canReject) && (
              <div className="gap-3 flex">
                {canApprove && (
                  <>
                    <button
                      type="button"
                      onClick={() => setViewerAction("approve")}
                      className={estiloDeBoton("exito")}
                    >
                      Aprobar
                      <CheckCheck className="w-4 h-4" />
                    </button>

                    <button
                      type="button"
                      onClick={() => setViewerAction("revision")}
                      className={estiloDeBoton("primario")}
                    >
                      A revisión
                      <Clock className="w-4 h-4" />
                    </button>

                    <button
                      type="button"
                      onClick={() => setViewerAction("expire")}
                      className={estiloDeBoton("secundario")}
                    >
                      Vencido
                      <AlertTriangle className="w-4 h-4" />
                    </button>
                  </>
                )}

                {canReject && (
                  <button
                    type="button"
                    onClick={() => setViewerAction("reject")}
                    className={estiloDeBoton("peligro")}
                  >
                    Rechazar
                    <XCircle className="w-4 h-4" />
                  </button>
                )}
              </div>
            )}

            {/* Action Form */}
            {viewerAction && (
              <div className="space-y-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                <div className="flex items-center gap-2">
                  {viewerAction === "approve" && (
                    <>
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <h3 className="font-medium text-green-700 dark:text-green-400">
                        Aprobar Documento
                      </h3>
                    </>
                  )}
                  {viewerAction === "reject" && (
                    <>
                      <XCircle className="w-5 h-5 text-red-600" />
                      <h3 className="font-medium text-red-700 dark:text-red-400">
                        Rechazar Documento
                      </h3>
                    </>
                  )}
                  {viewerAction === "revision" && (
                    <>
                      <Clock className="w-5 h-5 text-yellow-600" />
                      <h3 className="font-medium text-yellow-700 dark:text-yellow-400">
                        Enviar a Revisión
                      </h3>
                    </>
                  )}
                  {viewerAction === "expire" && (
                    <>
                      <AlertTriangle className="w-5 h-5 text-orange-600" />
                      <h3 className="font-medium text-orange-700 dark:text-orange-400">
                        Marcar como Vencido
                      </h3>
                    </>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {viewerAction === "reject"
                      ? "Motivo de rechazo *"
                      : "Observaciones (opcional)"}
                  </label>
                  <textarea
                    value={viewerObservations}
                    onChange={(e) => setViewerObservations(e.target.value)}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    placeholder={
                      viewerAction === "reject"
                        ? "Explica por qué se rechaza este documento..."
                        : "Agrega comentarios adicionales..."
                    }
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setViewerAction(null);
                      setViewerObservations("");
                    }}
                    className={estiloDeBoton("secundario", "flex-1")}
                    disabled={isProcessingAction}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={handleViewerActionSubmit}
                    disabled={
                      isProcessingAction ||
                      (viewerAction === "reject" && !viewerObservations.trim())
                    }
                    className={estiloDeBoton(viewerAction === "approve" ? "exito" : viewerAction === "reject" ? "peligro" : "primario", "flex-1")}
                  >
                    {isProcessingAction && <LoadingSpinner size="sm" />}
                    Confirmar
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={() => {
              setShowViewerModal(false);
              setViewerAction(null);
              setViewerObservations("");
            }}
            className={estiloDeBoton("secundario", "max-w-[100px] flex-1")}
            disabled={isProcessingAction}
          >
            Cerrar
          </button>
        </div>
      </Modal>

      {/* Not Applicable Modal */}
      <Modal
        isOpen={showNotApplicableModal}
        onClose={() => {
          setShowNotApplicableModal(false);
          setNotApplicableDocumentType(null);
          setNotApplicableReason("");
        }}
        title="Marcar Documento como No Aplicable"
        size="lg"
      >
        <div className="space-y-4">
          <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-4 rounded-r-lg">
            <div className="flex items-start gap-3">
              <FileText className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-blue-700 dark:text-blue-400 font-medium">
                  {notApplicableDocumentType?.nombre}
                </p>
                <p className="text-xs text-blue-600 dark:text-blue-500 mt-1">
                  {notApplicableDocumentType?.categoria
                    ? getCategoryName(notApplicableDocumentType.categoria)
                    : "Sin categoría"}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Al marcar este documento como <strong>"No Aplica"</strong>, el
              sistema lo registrará sin necesidad de archivo. Esta acción es
              útil cuando el documento no es requerido para este empleado en
              particular.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Motivo (opcional)
            </label>
            <textarea
              value={notApplicableReason}
              onChange={(e) => setNotApplicableReason(e.target.value)}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              placeholder="Ej: El empleado no requiere este documento por su tipo de contrato..."
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={() => {
                setShowNotApplicableModal(false);
                setNotApplicableDocumentType(null);
                setNotApplicableReason("");
              }}
              className={estiloDeBoton("secundario", "flex-1")}
              disabled={isMarkingNotApplicable}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleMarkAsNotApplicable}
              className={estiloDeBoton("primario")}
              disabled={isMarkingNotApplicable}
            >
              {isMarkingNotApplicable ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Marcando...
                </>
              ) : (
                "Marcar como N/A"
              )}
            </button>
          </div>
        </div>
      </Modal>

      {porEliminar && (
        <ConfirmarEliminacion
          titulo="¿Eliminar este documento?"
          nombre={
            porEliminar.documentType?.nombre ||
            porEliminar.fileName ||
            "el documento"
          }
          detalle="El tipo de documento vuelve a quedar pendiente de carga."
          trabajando={eliminando}
          onCancelar={() => setPorEliminar(null)}
          onConfirmar={() => void handleEliminar()}
        />
      )}

      {/* Notification Component */}
      <Notification
        type={notification.type}
        title={notification.title}
        message={notification.message}
        isVisible={notification.isVisible}
        onClose={hideNotification}
      />
    </div>
  );
};
